import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  CourseData,
  CourseMaterial,
  useCoursesLoaderData,
} from "./coursesLoader.ts";
import useToasts from "../../../root/core-ui/toast/useToasts.ts";
import { ProgramDecision } from "../../../../common/api/decimal/methods/course.ts";
import ProgramChangeSuccessToast from "./ProgramSwitcher/ProgramChangeSuccessToast.tsx";
import { useUpdateProgramChoiceFetcher } from "../actions/updateProgramChoiceAction.ts";
import DefaultErrorToast from "../common/DefaultErrorToast.tsx";
import deepmerge from "deepmerge";
import { useLayoutData } from "../layout/myCoursesLayoutLoader.ts";
import PrintStatusChangeToast from "./PrintStatusChangeToast.tsx";

function countMaterials(
  courseData: CourseData,
  statusToCount: CourseMaterial["materialStatus"] = "opted_in"
) {
  return courseData.courses
    .map((course) => course.courseMaterials)
    .flat()
    .reduce((total, material) => {
      if (material) {
        return material.materialStatus === statusToCount ? total + 1 : total;
      }
      return total;
    }, 0);
}

export default function useSwitchProgram(onDone?: () => void) {
  const { showToast, closeToast } = useToasts();
  const courseData = useCoursesLoaderData();
  const { config } = useLayoutData();
  const fetcher = useUpdateProgramChoiceFetcher();
  const oldCourseDataRef = useRef(deepmerge(courseData, {}));

  const announcePrintUpdates = useCallback(
    (courseData: CourseData) => {
      courseData.courses
        .map((course) => course.courseMaterials)
        .flat()
        .forEach((courseMaterial) => {
          if (courseMaterial && courseMaterial.materialFormat === "print") {
            showToast(
              <PrintStatusChangeToast
                vbid={courseMaterial.vbid}
                printStatus={courseMaterial.printStatus}
                courseMaterialId={courseMaterial.id}
                onClose={() => closeToast("printStatusChange")}
              />,
              { targetId: "printStatusChange" }
            );
          }
        });
    },
    [showToast, closeToast]
  );

  const changeProgram = useCallback(
    (programDecision: ProgramDecision) => {
      if (!courseData.currentTerm!.id || !programDecision) {
        showToast(<DefaultErrorToast />);
        return;
      }
      fetcher
        .submit(
          {
            programDecision,
            catalogId: courseData.currentTerm!.id,
          },
          {
            action: `/my-courses/program-decision`,
            method: "PUT",
          }
        )
        .then((result) => {
          if (!result.data?.ok) {
            showToast(<DefaultErrorToast />);
            onDone?.();
          }
        })
        .catch(() => {
          showToast(<DefaultErrorToast />);
          onDone?.();
        });
    },
    [courseData, fetcher, showToast, onDone]
  );

  useEffect(() => {
    function getMaterialCount() {
      // In EA Only always count all opt-able materials
      if (courseData.currentTerm!.institutionProgram === "equitable_access") {
        if (courseData.currentTerm!.programDecision === "no_program") {
          return countMaterials(courseData, "opted_out");
        }
        return countMaterials(courseData);
      }
      // When downgrading, don't count stuff you may already be opted of
      if (courseData.currentTerm!.programDecision === "no_program") {
        return countMaterials(oldCourseDataRef.current);
      }
      // When upgrading from nothing, look at the total amount of stuff
      // the new program contains
      if (
        oldCourseDataRef.current.currentTerm.programDecision === "no_program"
      ) {
        return countMaterials(courseData);
      }
      // Unsupported: When in FA IA/EA flips don't show counts
      return -1;
    }

    if (fetcher.state === "idle" && fetcher.data?.ok) {
      showToast(
        <ProgramChangeSuccessToast
          institutionProgram={courseData.currentTerm!.institutionProgram}
          oldProgram={oldCourseDataRef.current.currentTerm.programDecision}
          newProgram={courseData.currentTerm!.programDecision}
          materialCount={getMaterialCount()}
          config={config}
        />,
        // This is a unique ID so that there is one toast per program change
        // avoids duplicate toasts and allows stacking toasts rather than replacing
        {
          id: `programChangeSuccessId-${
            courseData.currentTerm!.programDecision
          }`,
        }
      );

      if (
        config.assets.showPrintPickup &&
        oldCourseDataRef.current.currentTerm.programDecision ===
          "equitable_access" &&
        courseData.currentTerm!.programDecision === "inclusive_access"
      ) {
        announcePrintUpdates(courseData);
      }

      if (onDone) {
        onDone();
      }
    }
  }, [showToast, onDone, fetcher, courseData, config, announcePrintUpdates]);

  const isProgramChangeProcessing = fetcher.state !== "idle";

  return useMemo(
    () => ({
      isProgramChangeProcessing,
      changeProgram,
    }),
    [isProgramChangeProcessing, changeProgram]
  );
}
